import React, {PureComponent} from 'react';
import {Route} from 'react-router';
import {Link} from 'react-router-dom';
import {NotificationManager} from 'react-notifications';
import styled from 'styled-components';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import withUrlState from '../../lib/with-url-state';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Tabs from '../../components/Tabs';
import Button from '../../components/Button';
import ListItem from '../../components/ListItem';
import Input from '../../components/Input';
import ListScreen from '../../components/ListScreen';
import Icon from '../../components/Icon';
import {Tag, AddTagButton, TagsContainer} from '../../components/TagComponents';
import CanEditUser from '../../security/rules/CanEditUser';
import UserFormModal from './UserFormModal';
import TagManagementModal from '../../components/TagManagementModal';
import CanCreateUser from "../../security/rules/CanCreateUser";
import Role from "../../lib/Role";
import search from "../../lib/search";
import {alphaSort} from '../../lib/sort';
import {T} from '../../components/I18n';
import * as tagHelpers from '../../lib/tag-helpers';

class UserTab extends PureComponent {

  state = {
    loading: true,
    failed: false,
    users: [],
    groups: [],
    tagManagementUser: null,
    tagUpdateCounter: 0,
    availableTags: []
  };

  async componentDidMount() {
    const {credentials} = this.props;

    try {
      const [groups, users] = await Promise.all([api.getUserGroups(credentials), api.getAllUsers(credentials)]);
      
      let availableTags = [];
      try {
        availableTags = await tagHelpers.fetchAvailableTags(credentials, 'user');
      } catch (error) {
        availableTags = tagHelpers.extractTagsFromItems(users);
      }
      
      this.setState({groups, users, availableTags});
    }
    catch(e) {
      this.setState({failed: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  handleCloseModal = () => {
    const {history, location: {search: queryString}} = this.props;
    history.push(`/users${queryString}`);
  }

  handleUserSaved = user => {
    this.setState(prevState => ({
      users: prevState.users.filter(u => u.id !== user.id).concat([user])
    }), this.handleCloseModal);
  }

  handleDeactivateUser = async user => {
    const {credentials} = this.props;
    const updatedUser = {...user, role: 'DEACTIVATED'};

    if (!confirm(`Are you sure you want to deactivate ${user.userName}? This cannot be undone.`)) { // eslint-disable-line
      return;
    }

    try {
      await api.updateUser(updatedUser, credentials);
      this.setState(prevState => ({
        users: prevState.users.filter(u => u.id !== user.id).concat([updatedUser])
      }));
      NotificationManager.success(<T isolate><T variable="username">{user.userName}</T> was deactivated</T>);
    }
    catch (e) {
      if (e.status === 409) {
        const count = (await e.json()).body.length;
        NotificationManager.error(<T isolate><T variable="username">{user.userName}</T> could not be deactivated because they're a reviewer on <T variable="formCount"> {count} </T> form(s)</T>);
      }
      else {
        NotificationManager.error(<T isolate><T variable="username">{user.userName}</T>  could not be deactivated! Make sure they're not a</T>);
      }
    }
  }

  handleSearchChange = e => {
    this.props.setUrlState({filter: e.target.value});
  }

  handleUserSortChange = e => {
    this.props.setUrlState({sort: e.target.value});
  }

  handleRoleFilterChange = e => {
    this.props.setUrlState({role: e.target.value});
  }

  handleTagFilterChange = e => {
    this.props.setUrlState({tag: e.target.value});
  }

  handleOpenTagManagement = user => {
    this.setState({tagManagementUser: user});
  }

  handleCloseTagManagement = () => {
    this.setState({tagManagementUser: null});
  }

  handleSaveTags = async (user, selectedTags, allAvailableTags, tagsToDelete = []) => {
    const {users} = this.state;
    const {credentials} = this.props;
    
    const currentTagNames = this.state.availableTags.map(tagHelpers.getTagName);
    const result = await tagHelpers.saveTags(user, selectedTags, allAvailableTags, tagsToDelete, credentials, 'user', currentTagNames);
    
    if (result.success) {
      const updatedUsers = tagHelpers.updateItemsAfterTagChanges(users, user, selectedTags, tagsToDelete);
      const newAvailableTags = await tagHelpers.fetchAvailableTags(credentials, 'user');
      this.setState({
        users: updatedUsers,
        availableTags: newAvailableTags,
        tagManagementUser: null,
        tagUpdateCounter: this.state.tagUpdateCounter + 1
      });
    }
  }

  renderEditModal = ({match: {params: {id}}}) => {
    const {groups, users} = this.state;
    if (id === 'create') {
      return (
        <UserFormModal
          open={true}
          groups={groups}
          onClose={this.handleCloseModal}
          onSave={this.handleUserSaved}
        />
      );
    }

    let user = null;

    if (id) {
      user = users.filter(user => user.id === id)[0];
    }

    return (
      <UserFormModal
        open={!!user}
        user={user}
        groups={groups}
        onClose={this.handleCloseModal}
        onSave={this.handleUserSaved}
      />
    );
  }

  render() {
    const {urlState: {sort, filter, role, tag}, location: {search: queryString}, user} = this.props;
    const {loading, error, users, tagManagementUser, tagUpdateCounter} = this.state;
    const filteredAndSortedUsers = users
      .filter(user => user.role !== Role.DEACTIVATED)
      .filter(user => search(user.userName, filter))
      .filter(filterUsersByRole(role))
      .filter(tagHelpers.createTagFilter(tag))
      .sort(alphaSort(sort, user => user.userName));
    const loggedInUser = user;
    
    const listKey = `${role}-${tag || 'all'}-${sort}-${tagUpdateCounter}`;

    return (
      <>
        <ListScreen
          key={listKey}
          title="Users"
          activeLink="Users"
          loading={loading && <Loading fullScreen />}
          error={error && <Error fullScreen />}
          empty={<>There are no users to display. { CanCreateUser(user)  &&
          <>Get started by <Link to={`/users/create${queryString}`}>creating a new user.</Link></>}</>}
          items={filteredAndSortedUsers}
          search={filter}
          onSearch={this.handleSearchChange}
          primaryAction={CanCreateUser(user) && <Button to={`/users/create${queryString}`}>New User</Button>}
          toolbarActions={[
            <Input id="username-sort" label="Sort by:" type="select" value={sort} onChange={this.handleUserSortChange}>
              <option value="asc">Alphabetical</option>
              <option value="desc">Reverse Alphabetical</option>
            </Input>,
            <Input id="role-filter" label="Role:" type="select" value={role} onChange={this.handleRoleFilterChange}>
              <option value="all">All Roles</option>
              <option value="admin">Administrator</option>
              <option value="manager">Manager</option>
              <option value="analyst">Analyst</option>
              <option value="reviewer">Reviewer</option>
              <option value="user">User</option>
            </Input>,
            <Input id="tag-filter" label="Tag:" type="select" value={tag || 'all'} onChange={this.handleTagFilterChange}>
              <option value="all">All Tags</option>
              {this.state.availableTags.map(tagObj => {
                const tagName = typeof tagObj === 'string' ? tagObj : tagObj.name;
                return <option key={tagName} value={tagName}>{tagName}</option>;
              })}
            </Input>
          ]}
          tabs={
            <Tabs>
              <Tabs.Tab active to="/users">Users</Tabs.Tab>
              <Tabs.Tab to="/users/groups">User Groups</Tabs.Tab>
            </Tabs>
          }>
          {(user, i) => (
            <ListItem
              first={i === 0}
              title={<T noTranslate>{user.userName}</T>}
              subtitle={
                <UserSubtitle>
                  <span>{getRoleFriendlyName(user.role)}</span>
                  <TagsContainer variant="user" hasContent={!!(user.tags && user.tags.length > 0)}>
                    {user.tags && user.tags.length > 0 && user.tags.map(tag => {
                      const tagName = typeof tag === 'string' ? tag : tag.name;
                      return (
                        <Tag key={tagName} onClick={() => this.handleTagFilterChange({target: {value: tagName}})}>{tagName}</Tag>
                      );
                    })}
                    {user.tags && user.tags.length > 0 && CanEditUser(loggedInUser, user) && (
                      <AddTagButton onClick={() => this.handleOpenTagManagement(user)}>
                        <Icon name="plus" size="1em" />
                      </AddTagButton>
                    )}
                  </TagsContainer>
                </UserSubtitle>
              }
              options={[
                CanEditUser(loggedInUser, user) && {title: 'Edit', to: `/users/${user.id + queryString}`},
                CanEditUser(loggedInUser, user) && {title: 'Modify Tags', onClick: () => this.handleOpenTagManagement(user)},
                CanEditUser(loggedInUser, user) && user.id !== loggedInUser.id && {title: 'Deactivate', onClick: () => this.handleDeactivateUser(user)}
              ].filter(Boolean)}
            />
          )}
        </ListScreen>
        <Route path="/users/:id?" component={this.renderEditModal} />
        <TagManagementModal
          open={!!tagManagementUser}
          onClose={this.handleCloseTagManagement}
          item={tagHelpers.normalizeItemForEditing(tagManagementUser)}
          itemName={tagManagementUser && tagManagementUser.userName}
          availableTags={this.state.availableTags}
          entityType="user"
          onSave={this.handleSaveTags}
        />
      </>
    );
  }
}

function getRoleFriendlyName(role) {
  switch (role) {
    case Role.ADMIN:
      return 'Administrator';
    case Role.REVIEWER:
      return 'Reviewer';
    case Role.MANAGER:
      return 'Manager';
    case Role.ANALYST:
      return 'Analyst';
    case Role.DEACTIVATED:
      return 'Deactivated';
    default:
      return Role.USER;
  }
}

function filterUsersByRole(role) {
  return user => role === 'all' || user.role === role.toUpperCase();
}

const defaultUrlState = {
  sort: 'asc',
  filter: '',
  role: 'all',
  tag: 'all'
};

const UserSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export default withUser(withUrlState(defaultUrlState)(UserTab));
